import { createContext, useContext, useState } from "react";

export const themes = {
  blue: {
    id: "blue", label: "Ocean Blue", emoji: "🔵",
    pageBg: "#f0f4f8",
    cardBg: "#ffffff",
    navBg: "rgba(255,255,255,0.96)",
    sectionAlt: "#e8eef4",
    heroBg: "linear-gradient(135deg, #0a2d4a 0%, #0d4c7a 50%, #1565a0 100%)",
    footerBg: "#0a2d4a",
    textPrimary: "#0d1b2a",
    textSecondary: "#3d5a73",
    textMuted: "#6b8fa8",
    textOnHero: "#ffffff",
    textOnHeroMuted: "rgba(255,255,255,0.72)",
    textOnDark: "#ffffff",
    textOnAccent: "#ffffff",
    primary: "#0d4c7a",
    primaryHover: "#0a3d63",
    accent: "#f59e0b",
    accentHover: "#d97706",
    accentText: "#92400e",
    border: "#d1dde8",
    borderHover: "#0d4c7a",
    shadow: "rgba(13,76,122,0.10)",
    shadowCard: "0 2px 12px rgba(13,76,122,0.08)",
    shadowHover: "0 8px 32px rgba(13,76,122,0.16)",
    badgeBg: "#e0edf8",
    badgeText: "#0d4c7a",
    tagBg: "#fef3c7",
    tagText: "#92400e",
  },
  gold: {
    id: "gold", label: "Royal Gold", emoji: "🟤",
    pageBg: "#fdf8f0",
    cardBg: "#ffffff",
    navBg: "rgba(255,255,255,0.96)",
    sectionAlt: "#f5ede0",
    heroBg: "linear-gradient(135deg, #4a2000 0%, #7c3a00 50%, #b45309 100%)",
    footerBg: "#4a2000",
    textPrimary: "#2d1600",
    textSecondary: "#6b3a1a",
    textMuted: "#9a6040",
    textOnHero: "#ffffff",
    textOnHeroMuted: "rgba(255,255,255,0.72)",
    textOnDark: "#ffffff",
    textOnAccent: "#ffffff",
    primary: "#92400e",
    primaryHover: "#7c3508",
    accent: "#1d6fa8",
    accentHover: "#155d8f",
    accentText: "#0d4c7a",
    border: "#e8d5bb",
    borderHover: "#92400e",
    shadow: "rgba(146,64,14,0.10)",
    shadowCard: "0 2px 12px rgba(146,64,14,0.08)",
    shadowHover: "0 8px 32px rgba(146,64,14,0.16)",
    badgeBg: "#fde8cc",
    badgeText: "#7c3508",
    tagBg: "#dbeafe",
    tagText: "#1e40af",
  },
  green: {
    id: "green", label: "Nature Green", emoji: "🟢",
    pageBg: "#f0faf4",
    cardBg: "#ffffff",
    navBg: "rgba(255,255,255,0.96)",
    sectionAlt: "#e0f5e8",
    heroBg: "linear-gradient(135deg, #052e16 0%, #065f46 50%, #059669 100%)",
    footerBg: "#052e16",
    textPrimary: "#052e16",
    textSecondary: "#166534",
    textMuted: "#4d7c5f",
    textOnHero: "#ffffff",
    textOnHeroMuted: "rgba(255,255,255,0.72)",
    textOnDark: "#ffffff",
    textOnAccent: "#ffffff",
    primary: "#065f46",
    primaryHover: "#054d38",
    accent: "#d97706",
    accentHover: "#b45309",
    accentText: "#92400e",
    border: "#bbddc8",
    borderHover: "#065f46",
    shadow: "rgba(6,95,70,0.10)",
    shadowCard: "0 2px 12px rgba(6,95,70,0.08)",
    shadowHover: "0 8px 32px rgba(6,95,70,0.16)",
    badgeBg: "#d1fae5",
    badgeText: "#065f46",
    tagBg: "#fef3c7",
    tagText: "#92400e",
  },
  dark: {
    id: "dark", label: "Dark Mode", emoji: "⚫",
    pageBg: "#0f172a",
    cardBg: "#1e293b",
    navBg: "rgba(15,23,42,0.97)",
    sectionAlt: "#162032",
    heroBg: "linear-gradient(135deg, #020817 0%, #0f172a 50%, #1e293b 100%)",
    footerBg: "#020817",
    textPrimary: "#f1f5f9",
    textSecondary: "#94a3b8",
    textMuted: "#64748b",
    textOnHero: "#ffffff",
    textOnHeroMuted: "rgba(255,255,255,0.65)",
    textOnDark: "#ffffff",
    textOnAccent: "#0f172a",
    primary: "#38bdf8",
    primaryHover: "#0ea5e9",
    accent: "#f59e0b",
    accentHover: "#d97706",
    accentText: "#f59e0b",
    border: "#2d3f55",
    borderHover: "#38bdf8",
    shadow: "rgba(0,0,0,0.30)",
    shadowCard: "0 2px 12px rgba(0,0,0,0.30)",
    shadowHover: "0 8px 32px rgba(0,0,0,0.45)",
    badgeBg: "#1e3a5f",
    badgeText: "#7dd3fc",
    tagBg: "#3d2a00",
    tagText: "#fcd34d",
  },
};

export const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(themes.blue);
  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes }}>
      <div style={{ background: theme.pageBg, minHeight: "100vh", transition: "background 0.35s ease" }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

/* ────────────────────────────────────────────────────────────
   PRODUCT IMAGES
   We use Unsplash CDN photos as the primary source. If any
   image fails to load, the <img> onError fallback swaps to
   a picsum.photos seed (always available) so the site never
   shows a broken image. Each product also has a gradient
   `fallbackBg` that shows behind the image while it loads.
   ──────────────────────────────────────────────────────── */

const img = (id, w = 800) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=${w}&q=80`;

export const products = [
  {
    id: 1, cat: "industrial", num: "01", name: "Iron Ore",
    desc: "High-grade iron ore for steel manufacturing. Available in lump and fines grades for smelting operations worldwide.",
    tag: "Export Ready", icon: "⚙️",
    image: img("1518709268805-4e9042af2176"),
    fallbackSeed: "ironore",
    fallbackBg: "linear-gradient(135deg,#475569,#1e293b)",
  },
  {
    id: 2, cat: "industrial", num: "02", name: "Sponge Iron",
    desc: "Direct reduced iron (DRI) used as substitute for scrap in steel making. Consistent quality and chemical composition.",
    tag: "Bulk Supply", icon: "🔩",
    image: img("1535813547-99c5e8e7d3fb"),
    fallbackSeed: "spongeiron",
    fallbackBg: "linear-gradient(135deg,#57534e,#292524)",
  },
  {
    id: 3, cat: "industrial", num: "03", name: "Iron Pellets",
    desc: "Spherical processed iron ore for blast furnaces and DRI plants. Uniform size for efficient furnace operation.",
    tag: "Standardized", icon: "🔮",
    image: img("1605559424843-9e4c228bf1c8"),
    fallbackSeed: "ironpellets",
    fallbackBg: "linear-gradient(135deg,#64748b,#334155)",
  },
  {
    id: 4, cat: "industrial", num: "04", name: "Coal",
    desc: "Thermal and coking coal for power generation and metallurgical industries. Various grades available globally.",
    tag: "Various Grades", icon: "🪨",
    image: img("1611273426858-450d8e3c9fce"),
    fallbackSeed: "coal",
    fallbackBg: "linear-gradient(135deg,#1f2937,#030712)",
  },
  {
    id: 5, cat: "industrial", num: "05", name: "Bauxite",
    desc: "High alumina bauxite for aluminium refining and refractory industries. Competitive pricing for bulk orders.",
    tag: "Competitive Pricing", icon: "🏔️",
    image: img("1567516099844-15b08c66ff0c"),
    fallbackSeed: "bauxite",
    fallbackBg: "linear-gradient(135deg,#a16207,#713f12)",
  },
  {
    id: 6, cat: "industrial", num: "06", name: "Quartz",
    desc: "High purity quartz for glass, ceramics, and electronics industries. Stringent purity standards maintained.",
    tag: "High Purity", icon: "💎",
    image: img("1518398046578-8cca57782e17"),
    fallbackSeed: "quartz",
    fallbackBg: "linear-gradient(135deg,#94a3b8,#475569)",
  },
  {
    id: 7, cat: "agro", num: "07", name: "IR64 Parboiled Rice",
    desc: "Premium parboiled rice retaining full nutrition. Ideal for African, Middle Eastern and Asian export markets.",
    tag: "Export Grade", icon: "🌾",
    image: img("1586201375761-83865001e31c"),
    fallbackSeed: "parboiled",
    fallbackBg: "linear-gradient(135deg,#d97706,#92400e)",
  },
  {
    id: 8, cat: "agro", num: "08", name: "IR64 White Rice",
    desc: "Long grain white rice with consistent aroma and texture. Widely demanded in international markets.",
    tag: "Bulk Orders", icon: "🍚",
    image: img("1536304993881-ff6e9eefa2a6"),
    fallbackSeed: "whiterice",
    fallbackBg: "linear-gradient(135deg,#fef3c7,#fbbf24)",
  },
  {
    id: 9, cat: "agro", num: "09", name: "Indian Sugar M30 & S30",
    desc: "Premium grade refined Indian sugar in M30 and S30 specifications. Ideal for food processing industries.",
    tag: "M30 & S30", icon: "🍬",
    image: img("1581798459219-318e76aecc7b"),
    fallbackSeed: "sugar",
    fallbackBg: "linear-gradient(135deg,#fef9c3,#facc15)",
  },
  {
    id: 10, cat: "spices", num: "10", name: "Turmeric & Red Chilli",
    desc: "High curcumin Rajasthani turmeric and premium red chilli with vibrant colour and intense flavour profile.",
    tag: "High Curcumin", icon: "🌶️",
    image: img("1532336414038-cf19250c5757"),
    fallbackSeed: "turmeric",
    fallbackBg: "linear-gradient(135deg,#dc2626,#7c2d12)",
  },
  {
    id: 11, cat: "spices", num: "11", name: "Cumin & Coriander",
    desc: "Whole and ground cumin and coriander seeds with rich aroma sourced directly from Rajasthan farms.",
    tag: "Farm Sourced", icon: "🌿",
    image: img("1596040033229-a9821ebd058d"),
    fallbackSeed: "cumin",
    fallbackBg: "linear-gradient(135deg,#a3a3a3,#525252)",
  },
  {
    id: 12, cat: "spices", num: "12", name: "Black Pepper & Cardamom",
    desc: "Premium Indian black pepper, green cardamom, and whole cloves packed to international food safety standards.",
    tag: "Premium Grade", icon: "🫙",
    image: img("1599909533736-e0bcb2eaa4e2"),
    fallbackSeed: "blackpepper",
    fallbackBg: "linear-gradient(135deg,#1c1917,#0c0a09)",
  },
];

export const stats = [
  { value: 20, suffix: "+", label: "Products Supplied" },
  { value: 3,  suffix: "",  label: "Major Categories" },
  { value: 100, suffix: "%", label: "Export Ready" },
  { value: 15, suffix: "+", label: "Years of Trust" },
];

export const catMeta = {
  industrial: { label: "Industrial Material", color: "#1d6fa8", bg: "#dbeafe", textColor: "#1e40af" },
  agro:       { label: "Agro Commodity",      color: "#059669", bg: "#d1fae5", textColor: "#065f46" },
  spices:     { label: "Export Spice",        color: "#dc2626", bg: "#fee2e2", textColor: "#991b1b" },
};

/* ────────────────────────────────────────────────────────────
   CONTACT — single source of truth for the whole site
   ──────────────────────────────────────────────────────── */
export const contactInfo = {
  // Phone numbers temporarily hidden — uncomment the line below and delete the empty array to restore.
  // phones: ["+91 96107 30213", "+91 91169 99564", "+91 81233 89649"],
  phones: [],
  email: "sourabh.dogger@gmail.com",
  // Two office addresses
  addresses: [
    {
      label: "Registered Office — Rajasthan",
      short: "Jodhpur, Rajasthan 342003",
      full: "Jodhpur, Rajasthan 342003, India",
    },
    {
      label: "Branch Office — Karnataka",
      short: "Hospet, Karnataka 583201",
      full: "Basaveshwar Badawani College Road, Hospet, Karnataka 583201, India",
    },
  ],
  hours: "Monday – Saturday\n9:00 AM – 6:00 PM IST",
};

/* ────────────────────────────────────────────────────────────
   SERVICES — adapted from S & R Odyssey "What We Do" deck
   ──────────────────────────────────────────────────────── */
export const services = [
  {
    icon: "🤝",
    title: "Global Trade Mediation",
    desc: "We connect verified suppliers with international buyers, acting as the bridge that keeps every transaction smooth, transparent and successful for both parties.",
    image: img("1521791136064-7986c2920216"),
    fallbackSeed: "tradedeal",
  },
  {
    icon: "🌾",
    title: "Product Sourcing & Trading",
    desc: "From grains, pulses and rice to industrial minerals, we source bulk commodities directly from origin and supply them to businesses across the world.",
    image: img("1574323347407-f5e1ad6d020b"),
    fallbackSeed: "sourcing",
  },
  {
    icon: "🚢",
    title: "Logistics & Coordination",
    desc: "From contract to container delivery, we coordinate freight, customs documentation and port handling to ensure goods reach their destination on time.",
    image: img("1494412519320-aa613dfb7738"),
    fallbackSeed: "logistics",
  },
  {
    icon: "📊",
    title: "Market Insights & Consultancy",
    desc: "We share live market intelligence on commodity trends, pricing and demand corridors so our partners can enter new markets with confidence.",
    image: img("1460925895917-afdab827c52f"),
    fallbackSeed: "insights",
  },
];

/* ────────────────────────────────────────────────────────────
   STRATEGIC PARTNER — S & R Odyssey Inc.
   ──────────────────────────────────────────────────────── */
export const strategicPartner = {
  name: "S & R Odyssey Inc.",
  tagline: "Your Global Trading Partner",
  country: "Canada",
  role: "International Trade & Brokerage Partner",
  desc: "S & R Odyssey Inc. is our Canada-based international trading and brokerage partner with a strong commercial network across North America, Africa, Asia and the Middle East. Together, we facilitate the supply and export of high-demand agricultural commodities and industrial products to key global markets — built on trust, transparency and shared compliance standards.",
  website: "www.sandrodyssey.com",
  highlights: [
    "Canada-based international trading & brokerage firm",
    "Active across North America, Africa, Asia and the Middle East",
    "Combined 20+ years of industry expertise on the board",
    "End-to-end facilitation: sourcing, logistics, secure payments",
  ],
};

/* ────────────────────────────────────────────────────────────
   GLOBAL PRESENCE — regions we actively serve
   ──────────────────────────────────────────────────────── */
export const regions = [
  { region: "South Asia",  countries: "India · Sri Lanka · Bangladesh", flag: "🌏" },
  { region: "Middle East", countries: "UAE · Saudi Arabia · Oman",       flag: "🕌" },
  { region: "Africa",      countries: "Kenya · Nigeria · South Africa",  flag: "🌍" },
  { region: "Southeast Asia", countries: "Malaysia · Vietnam · Indonesia", flag: "🏝️" },
  { region: "North America", countries: "Canada · USA (via S&R Odyssey)", flag: "🌎" },
  { region: "Europe",      countries: "Selected EU markets",              flag: "🏛️" },
];
